<template>
  <div
    v-if="shouldShowRecommendation"
    class="product-recommendations-element critical-component-clear"
    :class="{
      [$class('hidden')]: hidden,
      'non-dy-recommendations': productsSource === 'manual'
        || productsSource === 'shopify_native_rec',
      'dy-recommendations': productsSource === 'dynamic_yield',
      'no-shopify-recommendations': productsSource === 'shopify_native_rec'
        && products.length === 0,
    }"
  >
    <dynamic-yield-template
      v-if="productsSource === 'dynamic_yield' && settings.dynamicYieldTarget !== ''"
      :target="settings.dynamicYieldTarget"
      :settings="settings"
    />

    <product-carousel
      v-else-if="productsSource === 'manual'
        || productsSource === 'shopify_native_rec'
        || targetProducts.length > 0"
      :products="productsSource === 'shopify_native_rec' ? products : localProducts"
      :settings="settings"
      custom-report
      :intent="intent"
    />
  </div>
</template>

<script>

/**
 * Vue: Product recommendations (product-recommendations)
 * -----------------------------------------------------------------------------
 * Product recommendations section.
 *
 * @param {Array} carouselProducts - Products to be displayed in carousel.
 * @param {String} productsSource - Source of products in section.
 * @param {Object} settings - Object containing section settings.
 * @param {Object} targetProducts - Array of products from chosen collection.
 */
import { mapState } from 'vuex'

import DynamicYieldTemplate from '~async/dynamic-yield-template/dynamic-yield-template'
import ProductCarousel from '~global/product-carousel/product-carousel'
import { shopifyProductsByIdQuery } from '~/helpers/shopify'

export default {
  name: 'ProductRecommendations',
  components: {
    DynamicYieldTemplate,
    ProductCarousel,
  },

  props: {
    carouselProducts: {
      type: Array,
      default: () => [],
    },
    productsSource: {
      type: String,
      default: 'manual',
    },
    targetProducts: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: () => ({
        id: '',
        cardBackground: '--color-brand-white',
        cardLayout: 'regular',
        columnCount: 'small',
        dynamicYieldTarget: '',
        showBadges: false,
        showCarouselArrows: true,
        showFinalSale: false,
        showPreorderBadge: false,
        showSecondaryName: false,
        showWishlist: false,
        title: '',
        titleAlignment: 'default',
        titleClass: '',
        paddedImages: false,
        finalImageDesktop: null,
        finalImageMobile: null,
        finalImageText: '',
        finalImageUrl: null,
        finalImageOverlay: false,
        showFinalImage: false,
        zeroSearchRecommendation: false,
      }),
    },
    productId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      intent: '',
      products: [],
      dyLoadedDelayElapsed: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      dyCampaigns: (state) => state['dynamic-yield'].campaigns,
      zeroResults: (state) => state.search.zeroResults,
    }),

    /**
     * The DY campaign for this section, including Shopify section data.
     * @returns {Object} The DY campaign data with Shopify section data.
     */
    dyCampaign() {
      const dyCampaign = this.dyCampaigns[this.settings.dynamicYieldTarget] || null

      if (!dyCampaign) {
        return null
      }

      return {
        ...dyCampaign,
        section: this.settings,
      }
    },

    /**
     * Compute local products.
     * - Used to render loading products for Dynamic Yield or the carousel
     *   products, returns false if neither conditions are met.
     * @returns {Boolean|Array}
     */
    localProducts() {
      if (this.carouselProducts?.length) {
        return this.carouselProducts
      }

      if (
        (this.carouselProducts?.length === 0 || this.settings.dynamicYieldTarget === '') &&
        this.targetProducts.length > 0 && window.cnvs.page.type !== 'product'
      ) {
        return this.targetProducts
      }

      return false
    },

    /**
     * If the section should be hidden
     * @returns {Boolean}
     */
    hidden() {

      // If DY & no campaign,
      // always hide on PDPs & hide on non-PDPs when no targetProducts
      if (this.productsSource === 'dynamic_yield' &&
        (
          this.settings.dynamicYieldTarget === '' ||
          (
            this.dyLoadedDelayElapsed &&
            !this.dyCampaign
          )
        ) &&
        (window.cnvs.page.type === 'product' || this.targetProducts.length === 0)
      ) {
        return true
      }

      // If manual and no products chosen, hide on PDPs
      if (this.productsSource === 'manual' &&
        this.carouselProducts.length === 0 &&
        window.cnvs.page.type === 'product'
      ) {
        return true
      }

      // Hide if section is removed from DOM
      const section = document.querySelector(`[data-section-id="${this.settings.id}"]`)

      if (!section) {
        return true
      }

      return false
    },

    /**
     * Check if recommendation should be shown.
     *
     * Used for zero result page recommendation
     */
    shouldShowRecommendation() {
      return (this.zeroResults && this.settings.zeroSearchRecommendation) ||
        !this.settings.zeroSearchRecommendation
    },
  },

  watch: {
    productsSource: {
      handler() {
        this.fetchSource()
      },
      immediate: true,
    },
  },

  mounted() {
    // Failsafe in case DY never loads on the page
    setTimeout(() => {
      this.dyLoadedDelayElapsed = true
    }, this.$timing('message'))
  },

  methods: {

    /**
     * Checks source of products
     */
    async fetchSource() {
      // If set to native or when on PDPs,
      // fetch recommendation data from Shopify
      if (this.productsSource === 'shopify_native_rec' && window.cnvs.page.type === 'product') {
        await this.fetchShopifyRecommendations()
      // Otherwise, use provided data
      } else {
        // If not EN and product data comes from server, add EN metadata
        if (cnvs?.store?.language !== 'en' &&
          this.targetProducts.length > 0 &&
          this.targetProducts[0].liquid === true
        ) {
          this.queryProducts(this.targetProducts)
          return
        }

        // Otherwise use provided data as is
        this.products = this.targetProducts.length > 0 ? this.targetProducts : []
      }
    },

    /**
     * Fetch recommendation via Shopify recs API
     */
    async fetchShopifyRecommendations() {
      try {
        const response = await fetch(`${cnvs.routes.productRecommendations}?product_id=${this.productId}`)
        const data = await response.json()
        if (data.products?.length > 0) {
          this.intent = data.intent
          this.queryProducts(data.products)
        }
      } catch (error) {
        cnvs.ReportError('Failed to load products', error)
      }
    },

    /**
     * Request recommendation products via ID
     * @param productArray
     */
    async queryProducts(productArray) {
      const ids = productArray.map((product) => `gid://shopify/Product/${product.id}`)
      const results = await shopifyProductsByIdQuery(ids, false)
      this.products = results || []
    },
  },
}
</script>

<style lang="scss">
@import './product-recommendations';
</style>
